import React, { useCallback, useRef, useState } from 'react';
import type { Appointment } from 'pos-client/components/customScheduler/types/schedulerTypes';
import { getAppointmentLayoutStyle } from 'pos-client/components/customScheduler/utils/appointmentLayoutUtils.ts';

import styles from './ScheduleAppointment.module.css';

// Type aliases for cleaner code
type AppointmentMouseEvent = React.MouseEvent<HTMLDivElement>;
type AppointmentDragEvent = React.DragEvent<HTMLDivElement>;

interface ScheduleAppointmentProps {
  appointment: Appointment;
  appointmentHeightPx: number;
  offsetPx: number;
  layout?: { column: number; totalColumns: number; hidden: boolean };
  cellHeightPx: number;
  cellDuration: number;
  validatedConfig: {
    groups: string[];
    cellDuration: number;
  };
  justResized: string | number | null;
  onAppointmentClick?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
  }) => void;
  onAppointmentDblClick?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
  }) => void;
  onAppointmentContextMenu?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
    event: MouseEvent;
  }) => void;
  onAppointmentTooltipShowing?: (data: {
    appointmentData: Appointment;
    cancel: boolean;
  }) => void;
  onAppointmentDragStart: (
    e: AppointmentDragEvent,
    appointment: Appointment
  ) => void;
  onAppointmentDragOver: (e: AppointmentDragEvent) => void;
  onAppointmentDragEnd: (e: AppointmentDragEvent) => void;
  onAppointmentUpdating?: (data: {
    appointmentData: Appointment;
    newData: Partial<Appointment>;
    cancel: boolean;
  }) => void;
  onAppointmentUpdated?: (data: { appointmentData: Appointment }) => void;
  handleAppointmentDelete: (appointment: Appointment) => void;
  AppointmentComponent?: React.ComponentType<{
    data: Appointment;
    index: number;
    userIsDragging: boolean;
  }>;
  formatSlotTime: (date: Date) => string;
  setContextMenu: (menu: {
    visible: boolean;
    x: number;
    y: number;
    items: Array<{ id: string; label: string; action: () => void }>;
  }) => void;
  t: (key: string) => string;
  setJobDetailsModalData: (data: Record<string, unknown>) => void;
  setSelectedOrderDetailsForAssignTechnician: (data: {
    orderId: string;
    jobId: string;
  }) => void;
  canAuthorizeBlockedSlot: boolean;
  canAuthorizeOwnBlockedSlot: boolean;
  currentUserId?: string;
  unauthorizeBlockedSlot: (data: { blockedSlotId: string }) => void;
  authorizeBlockedSlot: (data: { blockedSlotId: string }) => void;
  userIsDragging: boolean;
}

const ScheduleAppointment: React.FC<ScheduleAppointmentProps> = ({
  appointment,
  appointmentHeightPx,
  offsetPx,
  layout,
  cellHeightPx,
  cellDuration,
  validatedConfig,
  justResized,
  onAppointmentClick,
  onAppointmentDblClick,
  onAppointmentContextMenu,
  onAppointmentTooltipShowing,
  onAppointmentDragStart,
  onAppointmentDragOver,
  onAppointmentDragEnd,
  onAppointmentUpdating,
  onAppointmentUpdated,
  handleAppointmentDelete,
  AppointmentComponent,
  formatSlotTime,
  setContextMenu,
  t,
  setJobDetailsModalData,
  setSelectedOrderDetailsForAssignTechnician,
  canAuthorizeBlockedSlot,
  canAuthorizeOwnBlockedSlot,
  currentUserId,
  unauthorizeBlockedSlot,
  authorizeBlockedSlot,
  userIsDragging,
}) => {
  const [resizeHandleStyle, setResizeHandleStyle] = useState({
    backgroundColor: 'rgba(0, 0, 0, 0)',
  });

  const resizeStateRef = useRef<{
    isResizing: boolean;
    startY: number;
    originalEndDate: Date;
    appointmentElement: HTMLElement | null;
  }>({
    isResizing: false,
    startY: 0,
    originalEndDate: new Date(),
    appointmentElement: null,
  });

  const layoutStyle = getAppointmentLayoutStyle(layout);

  const handleResizeMouseMove = useCallback(
    (moveEvent: MouseEvent) => {
      if (!resizeStateRef.current.isResizing) return;

      const { startY, originalEndDate, appointmentElement } =
        resizeStateRef.current;
      const deltaY = moveEvent.clientY - startY;
      const pixelsPerMinute = cellHeightPx / cellDuration;
      const deltaMinutes = Math.round(deltaY / pixelsPerMinute);

      const snappedDeltaMinutes =
        Math.round(deltaMinutes / validatedConfig.cellDuration) *
        validatedConfig.cellDuration;

      const newEndDate = new Date(originalEndDate);
      newEndDate.setMinutes(newEndDate.getMinutes() + snappedDeltaMinutes);

      const startDate = new Date(
        typeof appointment.startDateDisplay === 'string'
          ? appointment.startDateDisplay
          : appointment.startDate
      );

      if (newEndDate > startDate && appointmentElement) {
        const newHeight = Math.max(20, appointmentHeightPx + deltaY);
        appointmentElement.style.height = `${newHeight}px`;
      }
    },
    [
      appointment,
      appointmentHeightPx,
      cellDuration,
      cellHeightPx,
      validatedConfig.cellDuration,
    ]
  );

  const handleResizeMouseUp = useCallback(
    (upEvent: MouseEvent) => {
      if (!resizeStateRef.current.isResizing) return;

      const { startY, originalEndDate, appointmentElement } =
        resizeStateRef.current;
      resizeStateRef.current.isResizing = false;

      const deltaY = upEvent.clientY - startY;
      const pixelsPerMinute = cellHeightPx / cellDuration;
      const deltaMinutes = Math.round(deltaY / pixelsPerMinute);

      const snappedDeltaMinutes =
        Math.round(deltaMinutes / validatedConfig.cellDuration) *
        validatedConfig.cellDuration;

      const newEndDate = new Date(originalEndDate);
      newEndDate.setMinutes(newEndDate.getMinutes() + snappedDeltaMinutes);

      const startDate = new Date(
        typeof appointment.startDateDisplay === 'string'
          ? appointment.startDateDisplay
          : appointment.startDate
      );

      if (newEndDate > startDate) {
        if (onAppointmentUpdating) {
          onAppointmentUpdating({
            appointmentData: appointment,
            newData: {
              endDate: formatSlotTime(newEndDate),
            },
            cancel: false,
          });
        }

        if (onAppointmentUpdated) {
          const updatedAppointment: Appointment = {
            ...appointment,
            endDate: formatSlotTime(newEndDate),
          };

          onAppointmentUpdated({
            appointmentData: updatedAppointment,
          });
        }
      }

      if (appointmentElement) {
        appointmentElement.style.height = '';
      }
    },
    [
      appointment,
      cellDuration,
      cellHeightPx,
      formatSlotTime,
      onAppointmentUpdated,
      onAppointmentUpdating,
      validatedConfig.cellDuration,
    ]
  );

  React.useEffect(() => {
    document.addEventListener('mousemove', handleResizeMouseMove);
    document.addEventListener('mouseup', handleResizeMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  const handleResizeMouseDown = useCallback(
    (e: AppointmentMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const endDateVal =
        typeof appointment.endDateDisplay === 'string'
          ? appointment.endDateDisplay
          : appointment.endDate;
      const originalEndDate = new Date(endDateVal);
      const appointmentElement = e.currentTarget.parentElement;

      resizeStateRef.current = {
        isResizing: true,
        startY: e.clientY,
        originalEndDate,
        appointmentElement,
      };
    },
    [appointment]
  );

  const handleResizeClick = useCallback((e: AppointmentMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleResizeMouseEnter = useCallback(() => {
    setResizeHandleStyle({ backgroundColor: 'rgba(0, 0, 0, 0)' });
  }, []);

  const handleResizeMouseLeave = useCallback(() => {
    setResizeHandleStyle({ backgroundColor: 'rgba(0, 0, 0, 0)' });
  }, []);

  const handleContextMenu = useCallback(
    (e: AppointmentMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (appointment.text === 'Blocked') {
        setContextMenu({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          items: [
            {
              id: 'edit-blocked-slot',
              label: t('scheduling.blockedSlot.editBlockedSlot'),
              action: () => {
                onAppointmentContextMenu?.({
                  appointmentData: appointment,
                  appointmentElement: e.currentTarget,
                  event: e.nativeEvent as MouseEvent,
                });
              },
            },
            {
              id: 'blockslot-authorise-toggle',
              label:
                appointment.state === 'Authorized'
                  ? t('common.buttons.unauthorise')
                  : t('common.buttons.authorise'),
              action: () => {
                const jobModel = appointment as Record<string, unknown>;
                const canApproveOwnBlockedSlot =
                  canAuthorizeOwnBlockedSlot &&
                  currentUserId === jobModel.createdBy;
                const isAuthorizedBlockedSlot =
                  typeof jobModel.state === 'string' &&
                  jobModel.state.toLowerCase() === 'authorized';

                if (canAuthorizeBlockedSlot || canApproveOwnBlockedSlot) {
                  if (isAuthorizedBlockedSlot) {
                    unauthorizeBlockedSlot({
                      blockedSlotId: String(appointment.id),
                    });
                  } else {
                    authorizeBlockedSlot({
                      blockedSlotId: String(appointment.id),
                    });
                  }
                }
              },
            },
          ],
        });
      } else {
        setContextMenu({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          items: [
            {
              id: 'job-details',
              label: t('jobs.jobDetails'),
              action: () => {
                setJobDetailsModalData({
                  ...appointment,
                  id: String(appointment.id),
                  startDate: String(appointment.startDate || ''),
                  endDate: String(appointment.endDate || ''),
                  startDateDisplay: String(
                    appointment.startDateDisplay || appointment.startDate
                  ),
                  endDateDisplay: String(
                    appointment.endDateDisplay || appointment.endDate
                  ),
                  text: String(appointment.text || appointment.title || ''),
                  state: String(appointment.state || 'to-do'),
                  laneName:
                    typeof appointment.laneName === 'string'
                      ? appointment.laneName
                      : null,
                  laneId: String(appointment.laneId || ''),
                  duration: String(appointment.duration || ''),
                  jobReference: String(appointment.jobReference || ''),
                  jobId: String(appointment.jobId ?? ''),
                  orderId: String(appointment.orderId ?? ''),
                  orderReference: String(appointment.orderReference ?? ''),
                  jobType: String(appointment.jobType ?? ''),
                  serviceType:
                    (appointment.serviceType as 'Mobile' | 'Static') ||
                    'Mobile',
                  allowResizingReschedule: Boolean(
                    appointment.allowResizingReschedule
                  ),
                  allowDragAndDropReschedule: Boolean(
                    appointment.allowDragAndDropReschedule
                  ),
                  fulfilmentTo:
                    typeof appointment.fulfilmentTo === 'object' &&
                    appointment.fulfilmentTo !== null
                      ? appointment.fulfilmentTo
                      : { reference: null, dataType: null },
                  customer:
                    typeof appointment.customer === 'object' &&
                    appointment.customer !== null
                      ? appointment.customer
                      : { customerId: '' },
                });
                setSelectedOrderDetailsForAssignTechnician({
                  orderId: String(appointment.orderId ?? ''),
                  jobId: String(appointment.jobId ?? ''),
                });
              },
            },
            {
              id: 'reschedule-appointment',
              label: t('scheduling.reschedule'),
              action: () => {
                onAppointmentContextMenu?.({
                  appointmentData: appointment,
                  appointmentElement: e.currentTarget,
                  event: e.nativeEvent as MouseEvent,
                });
              },
            },
          ],
        });
      }
    },
    [
      appointment,
      authorizeBlockedSlot,
      canAuthorizeBlockedSlot,
      canAuthorizeOwnBlockedSlot,
      currentUserId,
      onAppointmentContextMenu,
      setContextMenu,
      setJobDetailsModalData,
      setSelectedOrderDetailsForAssignTechnician,
      t,
      unauthorizeBlockedSlot,
    ]
  );

  return (
    <div
      className={styles.appointment}
      style={{
        position: 'absolute',
        top: `${offsetPx}px`,
        height: `${appointmentHeightPx}px`,
        padding: '2px',
        cursor: 'pointer',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        borderRadius: '0.6em',
        overflow: 'visible',
        margin: '0px 10px 0px 10px',
        ...layoutStyle,
      }}
      draggable
      onClick={(e) => {
        if (justResized === appointment.id) {
          return;
        }
        onAppointmentClick?.({
          appointmentData: appointment,
          appointmentElement: e.currentTarget,
        });
      }}
      onDoubleClick={(e) => {
        onAppointmentDblClick?.({
          appointmentData: appointment,
          appointmentElement: e.currentTarget,
        });
      }}
      onContextMenu={handleContextMenu}
      onMouseEnter={(e) => {
        e.currentTarget.title = '';

        onAppointmentTooltipShowing?.({
          appointmentData: appointment,
          cancel: false,
        });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          e.stopPropagation();
          handleAppointmentDelete(appointment);
        }
      }}
      onDragStart={(e) => onAppointmentDragStart(e, appointment)}
      onDragOver={onAppointmentDragOver}
      onDragEnd={onAppointmentDragEnd}
      tabIndex={0}
    >
      <div className={styles.contentWrapper}>
        {AppointmentComponent ? (
          <AppointmentComponent
            data={appointment}
            index={0}
            userIsDragging={userIsDragging}
          />
        ) : (
          <div className={styles.defaultContent}>{appointment.title}</div>
        )}
      </div>
      <div
        className={styles.resizeHandle}
        style={resizeHandleStyle}
        onClick={handleResizeClick}
        onMouseDown={handleResizeMouseDown}
        onMouseEnter={handleResizeMouseEnter}
        onMouseLeave={handleResizeMouseLeave}
      />
    </div>
  );
};

export default React.memo(ScheduleAppointment);
